/**
 * 계정 화면의 문구 — **여기가 정본이다.**
 *
 * 화면이 앱에서 웹으로 옮겨 온 이유가 "문구 한 줄을 고치는 데 앱 배포가 끼지 않게"였다
 * (2026-08-26 사용자 지시). 그러니 말을 고치는 자리는 이 파일 하나이고, 앱에는 웹이 안 뜰
 * 때의 네 줄만 남아 있다(`auth-native` 의 `accountChromeLabels.ts`).
 *
 * ## 언어를 늘리는 것도 이제 웹 배포다
 *
 * 지금 한국어·영어·일본어 셋이다. 앱 시절에는 한국어 하나였으니 줄어든 것은 없고,
 * **여기 표에 한 벌을 더하면 앱을 다시 올리지 않고 그 언어가 는다.** 모르는 태그는
 * 영어로 내려간다 — 한국어로 내려가면 못 읽는 사람에게 못 읽는 화면을 주는 것이다.
 */
export type AccountLanguage = 'ko' | 'en' | 'ja'

export interface AccountLabels {
  title: string
  /** 앱 밖(브라우저)에서 열었을 때 */
  appOnlyTitle: string
  appOnlyBody: string

  loadFailedTitle: string
  loadFailedBody: string
  retry: string
  loading: string

  editProfile: string
  changePassword: string
  changePasswordHint: string
  linkedAccounts: string
  logout: string
  deleteAccount: string

  /** 프로필 수정 */
  editTitle: string
  nameLabel: string
  nameHelper: string
  namePlaceholder: string
  nameRequired: string
  emailLabel: string
  emailPlaceholder: string
  emailInvalid: string
  save: string
  saved: string

  /** 사진 */
  photo: string
  photoFromCamera: string
  photoFromLibrary: string
  photoRemove: string
  photoRemoveConfirm: string

  /** 비밀번호 */
  passwordTitle: string
  currentPassword: string
  newPassword: string
  newPasswordHelper: (min: number) => string
  confirmPassword: string
  currentRequired: string
  tooShort: (min: number) => string
  mismatch: string
  samePassword: string
  passwordChanged: string
  passwordChangedBody: string
  change: string

  /** 연결된 계정 */
  linkedTitle: string
  linkedReadOnly: string
  connected: string
  notConnected: string
  passwordLogin: string

  /** 탈퇴 */
  deleteTitle: string
  deleteConfirm: string
  deleteCancel: string
  deleting: string

  /** 실패 사유 */
  errorNetwork: string
  errorDenied: string
  errorConflict: string
  errorInvalid: string
  errorUnsupported: string

  back: string
}

const KO: AccountLabels = {
  title: '내 계정',
  appOnlyTitle: '앱에서 열어 주세요',
  appOnlyBody: '이 화면은 앱 안에서만 열려요. 앱의 설정에서 “내 계정”을 눌러 주세요.',

  loadFailedTitle: '계정 정보를 불러오지 못했어요',
  loadFailedBody: '연결을 확인하고 다시 시도해 주세요.',
  retry: '다시 시도',
  loading: '불러오는 중',

  editProfile: '프로필 수정',
  changePassword: '비밀번호 변경',
  changePasswordHint: '주기적으로 바꾸면 안전해요',
  linkedAccounts: '연결된 계정',
  logout: '로그아웃',
  deleteAccount: '회원 탈퇴',

  editTitle: '프로필 수정',
  nameLabel: '이름',
  nameHelper: '목록과 알림에 이 이름이 보여요.',
  namePlaceholder: '이름',
  nameRequired: '이름을 입력해 주세요.',
  emailLabel: '이메일',
  emailPlaceholder: 'name@example.com',
  emailInvalid: '이메일 주소를 다시 확인해 주세요.',
  save: '저장',
  saved: '저장했어요',

  photo: '프로필 사진',
  photoFromCamera: '사진 촬영',
  photoFromLibrary: '앨범에서 선택',
  photoRemove: '사진 삭제',
  photoRemoveConfirm: '프로필 사진을 지울까요?',

  passwordTitle: '비밀번호 변경',
  currentPassword: '현재 비밀번호',
  newPassword: '새 비밀번호',
  newPasswordHelper: (min) => `${min}자 이상`,
  confirmPassword: '새 비밀번호 확인',
  currentRequired: '현재 비밀번호를 입력해 주세요.',
  tooShort: (min) => `새 비밀번호는 ${min}자 이상으로 해주세요.`,
  mismatch: '새 비밀번호가 서로 달라요.',
  samePassword: '지금 쓰는 비밀번호와 달라야 해요.',
  passwordChanged: '비밀번호를 바꿨어요',
  passwordChangedBody: '다음 로그인부터 새 비밀번호를 써주세요.',
  change: '변경',

  linkedTitle: '연결된 계정',
  linkedReadOnly: '연결을 끊는 것은 아직 여기서 할 수 없어요. 문의로 알려 주시면 도와드려요.',
  connected: '연결됨',
  notConnected: '연결 안 됨',
  passwordLogin: '아이디/비밀번호',

  deleteTitle: '정말 탈퇴할까요?',
  deleteConfirm: '탈퇴',
  deleteCancel: '취소',
  deleting: '탈퇴하는 중',

  errorNetwork: '연결을 확인하고 다시 시도해 주세요.',
  errorDenied: '다시 로그인한 뒤 시도해 주세요.',
  errorConflict: '이미 쓰고 있는 이메일이에요.',
  errorInvalid: '입력한 값을 다시 확인해 주세요.',
  errorUnsupported: '이 앱에서는 아직 할 수 없어요.',

  back: '뒤로',
}

const EN: AccountLabels = {
  title: 'My account',
  appOnlyTitle: 'Open this from the app',
  appOnlyBody: 'This page only works inside the app. Open “My account” from the app’s settings.',

  loadFailedTitle: 'Could not load your account',
  loadFailedBody: 'Check your connection and try again.',
  retry: 'Try again',
  loading: 'Loading',

  editProfile: 'Edit profile',
  changePassword: 'Change password',
  changePasswordHint: 'Changing it now and then keeps you safer',
  linkedAccounts: 'Linked accounts',
  logout: 'Sign out',
  deleteAccount: 'Delete account',

  editTitle: 'Edit profile',
  nameLabel: 'Name',
  nameHelper: 'This name appears in lists and notifications.',
  namePlaceholder: 'Name',
  nameRequired: 'Please enter a name.',
  emailLabel: 'Email',
  emailPlaceholder: 'name@example.com',
  emailInvalid: 'Please check the email address.',
  save: 'Save',
  saved: 'Saved',

  photo: 'Profile photo',
  photoFromCamera: 'Take a photo',
  photoFromLibrary: 'Choose from library',
  photoRemove: 'Remove photo',
  photoRemoveConfirm: 'Remove your profile photo?',

  passwordTitle: 'Change password',
  currentPassword: 'Current password',
  newPassword: 'New password',
  newPasswordHelper: (min) => `At least ${min} characters`,
  confirmPassword: 'Confirm new password',
  currentRequired: 'Please enter your current password.',
  tooShort: (min) => `Use at least ${min} characters.`,
  mismatch: 'The new passwords do not match.',
  samePassword: 'It must differ from your current password.',
  passwordChanged: 'Password changed',
  passwordChangedBody: 'Use the new password from your next sign-in.',
  change: 'Change',

  linkedTitle: 'Linked accounts',
  linkedReadOnly: 'Unlinking is not available here yet. Send us a support message and we will help.',
  connected: 'Connected',
  notConnected: 'Not connected',
  passwordLogin: 'ID and password',

  deleteTitle: 'Delete your account?',
  deleteConfirm: 'Delete',
  deleteCancel: 'Cancel',
  deleting: 'Deleting',

  errorNetwork: 'Check your connection and try again.',
  errorDenied: 'Please sign in again and retry.',
  errorConflict: 'That email is already in use.',
  errorInvalid: 'Please check what you entered.',
  errorUnsupported: 'This app cannot do that yet.',

  back: 'Back',
}

const JA: AccountLabels = {
  title: 'アカウント',
  appOnlyTitle: 'アプリから開いてください',
  appOnlyBody: 'この画面はアプリ内でのみ開きます。アプリの設定から「アカウント」を開いてください。',

  loadFailedTitle: 'アカウント情報を読み込めませんでした',
  loadFailedBody: '接続を確認して再度お試しください。',
  retry: '再試行',
  loading: '読み込み中',

  editProfile: 'プロフィール編集',
  changePassword: 'パスワード変更',
  changePasswordHint: '定期的に変えると安全です',
  linkedAccounts: '連携アカウント',
  logout: 'ログアウト',
  deleteAccount: '退会',

  editTitle: 'プロフィール編集',
  nameLabel: '名前',
  nameHelper: '一覧や通知にこの名前が表示されます。',
  namePlaceholder: '名前',
  nameRequired: '名前を入力してください。',
  emailLabel: 'メールアドレス',
  emailPlaceholder: 'name@example.com',
  emailInvalid: 'メールアドレスをご確認ください。',
  save: '保存',
  saved: '保存しました',

  photo: 'プロフィール写真',
  photoFromCamera: '写真を撮る',
  photoFromLibrary: 'アルバムから選ぶ',
  photoRemove: '写真を削除',
  photoRemoveConfirm: 'プロフィール写真を削除しますか？',

  passwordTitle: 'パスワード変更',
  currentPassword: '現在のパスワード',
  newPassword: '新しいパスワード',
  newPasswordHelper: (min) => `${min}文字以上`,
  confirmPassword: '新しいパスワード（確認）',
  currentRequired: '現在のパスワードを入力してください。',
  tooShort: (min) => `新しいパスワードは${min}文字以上にしてください。`,
  mismatch: '新しいパスワードが一致しません。',
  samePassword: '現在のパスワードとは別のものにしてください。',
  passwordChanged: 'パスワードを変更しました',
  passwordChangedBody: '次回のログインから新しいパスワードをお使いください。',
  change: '変更',

  linkedTitle: '連携アカウント',
  linkedReadOnly: '連携の解除はまだここではできません。お問い合わせいただければ対応します。',
  connected: '連携済み',
  notConnected: '未連携',
  passwordLogin: 'ID・パスワード',

  deleteTitle: '本当に退会しますか？',
  deleteConfirm: '退会',
  deleteCancel: 'キャンセル',
  deleting: '退会処理中',

  errorNetwork: '接続を確認して再度お試しください。',
  errorDenied: '再度ログインしてからお試しください。',
  errorConflict: 'そのメールアドレスは既に使われています。',
  errorInvalid: '入力内容をご確認ください。',
  errorUnsupported: 'このアプリではまだできません。',

  back: '戻る',
}

const TABLE: Record<AccountLanguage, AccountLabels> = { ko: KO, en: EN, ja: JA }

/** 모르는 태그는 영어다 — 못 읽는 사람에게 못 읽는 화면을 주지 않기 위해서다 */
export function accountLanguageOf(raw: string | null | undefined): AccountLanguage {
  const base = (raw ?? '').trim().toLowerCase().split(/[-_]/)[0] ?? ''
  return base === 'ko' || base === 'ja' ? base : 'en'
}

export function accountLabels(language: AccountLanguage): AccountLabels {
  return TABLE[language]
}
